import React, { useMemo } from 'react';
import { motion } from 'framer-motion';
import { ServiceTimeline, TimelineStep } from './ServiceTimeline';
import { Button } from '../ui/Button';
import { useLanguage } from '../../context/LanguageContext';
import styles from './MarkdownPage.module.css';

interface MarkdownPageProps {
  title: string;
  content: string;
  eyebrow?: string;
  heroImage?: string;
  timelineData?: {
    title?: string;
    steps: TimelineStep[];
  };
}

const renderInline = (text: string): (string | JSX.Element)[] => {
  const parts = text.split(/(\*\*[^*]+\*\*)/g);
  return parts.map((part, index) => {
    if (part.startsWith('**') && part.endsWith('**')) {
      return (
        <strong key={`bold-${index}`} className={styles.bold}>
          {part.slice(2, -2)}
        </strong>
      );
    }
    return part;
  });
};

const renderMarkdown = (markdown: string): JSX.Element[] => {
  const lines = markdown.split('\n');
  const elements: JSX.Element[] = [];
  let listBuffer: string[] = [];

  const flushList = (keyPrefix: string) => {
    if (listBuffer.length) {
      elements.push(
        <ul key={`${keyPrefix}-list-${elements.length}`} className={styles.list}>
          {listBuffer.map((item, idx) => (
            <li key={`${keyPrefix}-item-${idx}`}>{renderInline(item)}</li>
          ))}
        </ul>
      );
      listBuffer = [];
    }
  };

  lines.forEach((line, index) => {
    const trimmed = line.trim();

    if (!trimmed) {
      flushList(`line-${index}`);
      return;
    }

    if (trimmed.startsWith('# ')) {
      flushList(`line-${index}`);
      elements.push(
        <h1 key={`h1-${index}`} className={styles.h1}>
          {renderInline(trimmed.slice(2).trim())}
        </h1>
      );
      return;
    }

    if (trimmed.startsWith('## ')) {
      flushList(`line-${index}`);
      elements.push(
        <h2 key={`h2-${index}`} className={styles.h2}>
          {renderInline(trimmed.slice(3).trim())}
        </h2>
      );
      return;
    }

    if (trimmed.startsWith('- ')) {
      listBuffer.push(trimmed.slice(2));
      return;
    }

    flushList(`line-${index}`);
    elements.push(
      <p key={`p-${index}`} className={styles.paragraph}>
        {renderInline(trimmed)}
      </p>
    );
  });

  flushList('final');
  return elements;
};

export const MarkdownPage: React.FC<MarkdownPageProps> = ({
  title,
  content,
  eyebrow,
  heroImage,
  timelineData,
}) => {
  const { language } = useLanguage();
  const body = useMemo(() => renderMarkdown(content), [content]);
  const cta = {
    en: {
      title: 'Next steps',
      desc: 'Want a production-ready path? Book a discovery call or review engagement and trust details.',
      primary: 'Book a Discovery Call',
      secondary: 'Engagement & Pricing',
      trust: 'Trust & Compliance',
      all: 'All insights',
    },
    de: {
      title: 'Nächste Schritte',
      desc: 'Sie wollen einen produktionsreifen Pfad? Buchen Sie ein Discovery Gespräch oder sehen Sie Engagement/Trust Details.',
      primary: 'Discovery Call buchen',
      secondary: 'Engagement & Pricing',
      trust: 'Trust & Compliance',
      all: 'Alle Insights',
    },
  }[language];

  return (
    <div className={styles.page}>
      <section
        className={`${styles.hero} ${heroImage ? styles['hero--withImage'] : ''}`}
        style={heroImage ? { backgroundImage: `url(${heroImage})` } : undefined}
      >
        <div className={styles.hero__overlay} />
        <motion.div
          className={styles.hero__container}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.15 }}
        >
          {eyebrow && <span className={styles.eyebrow}>{eyebrow}</span>}
          <h1 className={styles.title}>{title}</h1>
        </motion.div>
      </section>

      <section className={styles.content}>
        <div className={styles.content__container}>
          {body}

          {timelineData && (
            <ServiceTimeline title={timelineData.title} steps={timelineData.steps} />
          )}

          <div className={styles.cta}>
            <div className={styles.cta__text}>
              <h3 className={styles.cta__title}>{cta.title}</h3>
              <p className={styles.cta__desc}>{cta.desc}</p>
            </div>
            <div className={styles.cta__grid}>
              <div className={styles.cta__actions}>
                <Button variant="primary" size="md" to="/consultation">{cta.primary}</Button>
                <Button variant="secondary" size="md" to="/engagement">{cta.secondary}</Button>
                <Button variant="ghost" size="md" to="/trust">{cta.trust}</Button>
                <Button variant="ghost" size="md" to="/insights">{cta.all}</Button>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};
